import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { surveyReportApi } from '../api/surveyReportApi';
import { useNotifications } from '../../../shared/components/notifications/useNotifications';
import { CreateSurveyReportDTO, UpdateSurveyReportDTO, UseSurveyReportOptions } from '../types';

// Query Keys Factory
export const surveyReportQueryKeys = {
  all: ['surveyReports'] as const,
  reports: () => [...surveyReportQueryKeys.all, 'reports'] as const,
  detail: (reportId: string) => [...surveyReportQueryKeys.reports(), reportId] as const,
  byInstanceId: (instanceId: string) =>
    [...surveyReportQueryKeys.all, 'instance', instanceId] as const,
  byAuditId: (auditId: string) => [...surveyReportQueryKeys.all, 'audit', auditId] as const,
  bySurveyId: (surveyId: string) => [...surveyReportQueryKeys.all, 'survey', surveyId] as const,
  bySubjectId: (subjectId: string) => [...surveyReportQueryKeys.all, 'subject', subjectId] as const,
  statistics: (reportId: string) =>
    [...surveyReportQueryKeys.detail(reportId), 'statistics'] as const,
  permissions: (reportId: string) =>
    [...surveyReportQueryKeys.detail(reportId), 'permissions'] as const,
  exports: (reportId: string) => [...surveyReportQueryKeys.detail(reportId), 'exports'] as const,
};

// Get Survey Report by ID
export const useSurveyReport = (reportId: string, options?: UseSurveyReportOptions) => {
  return useQuery({
    queryKey: surveyReportQueryKeys.detail(reportId),
    queryFn: () => surveyReportApi.getSurveyReport(reportId),
    enabled: options?.enabled !== false && !!reportId,
    staleTime: options?.staleTime || 5 * 60 * 1000, // 5 minutes
    refetchInterval: options?.refetchInterval,
    retry: (failureCount, error: any) => {
      // Don't retry if report not found
      if (error?.response?.status === 404) return false;
      return failureCount < 3;
    },
  });
};

// Get Report by Instance ID
export const useReportByInstanceId = (instanceId: string, options?: UseSurveyReportOptions) => {
  return useQuery({
    queryKey: surveyReportQueryKeys.byInstanceId(instanceId),
    queryFn: () => surveyReportApi.getReportByInstanceId(instanceId),
    enabled: options?.enabled !== false && !!instanceId,
    staleTime: options?.staleTime || 5 * 60 * 1000,
    retry: (failureCount, error: any) => {
      if (error?.response?.status === 404) return false;
      return failureCount < 3;
    },
  });
};

// Get Report by Audit ID
export const useReportByAuditId = (auditId: string, options?: UseSurveyReportOptions) => {
  return useQuery({
    queryKey: surveyReportQueryKeys.byAuditId(auditId),
    queryFn: () => surveyReportApi.getReportByAuditId(auditId),
    enabled: options?.enabled !== false && !!auditId,
    staleTime: options?.staleTime || 5 * 60 * 1000,
    retry: (failureCount, error: any) => {
      if (error?.response?.status === 404) return false;
      return failureCount < 3;
    },
  });
};

// Get Reports by Subject ID
export const useReportsBySubjectId = (subjectId: string, options?: UseSurveyReportOptions) => {
  return useQuery({
    queryKey: surveyReportQueryKeys.bySubjectId(subjectId),
    queryFn: () => surveyReportApi.getReportsBySubjectId(subjectId),
    enabled: options?.enabled !== false && !!subjectId,
    staleTime: options?.staleTime || 2 * 60 * 1000, // 2 minutes for lists
    refetchInterval: options?.refetchInterval,
  });
};

// Create Report
export const useCreateReport = () => {
  const queryClient = useQueryClient();
  const { success, error } = useNotifications();

  return useMutation({
    mutationFn: (data: CreateSurveyReportDTO) => surveyReportApi.createReport(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: surveyReportQueryKeys.all });
      success('Report created successfully');
    },
    onError: (err: any) => {
      const errorMessage = err?.response?.data?.message || 'Failed to create report';
      error(errorMessage);
    },
  });
};

// Update Report
export const useUpdateReport = () => {
  const queryClient = useQueryClient();
  const { success, error } = useNotifications();

  return useMutation({
    mutationFn: ({ reportId, data }: { reportId: string; data: UpdateSurveyReportDTO }) =>
      surveyReportApi.updateReport(reportId, data),
    onSuccess: (response, { reportId }) => {
      queryClient.invalidateQueries({ queryKey: surveyReportQueryKeys.detail(reportId) });
      queryClient.invalidateQueries({ queryKey: surveyReportQueryKeys.reports() });
      success('Report updated successfully');
    },
    onError: (err: any) => {
      const errorMessage = err?.response?.data?.message || 'Failed to update report';
      error(errorMessage);
    },
  });
};

// Delete Report
export const useDeleteReport = () => {
  const queryClient = useQueryClient();
  const { success, error } = useNotifications();

  return useMutation({
    mutationFn: (reportId: string) => surveyReportApi.deleteReport(reportId),
    onSuccess: (response, reportId) => {
      queryClient.removeQueries({ queryKey: surveyReportQueryKeys.detail(reportId) });
      queryClient.invalidateQueries({ queryKey: surveyReportQueryKeys.reports() });
      success('Report deleted successfully');
    },
    onError: (err: any) => {
      const errorMessage = err?.response?.data?.message || 'Failed to delete report';
      error(errorMessage);
    },
  });
};

// Generate Report from Audit
export const useGenerateReport = () => {
  const queryClient = useQueryClient();
  const { success, error } = useNotifications();

  return useMutation({
    mutationFn: ({
      auditId,
      data,
    }: {
      auditId: string;
      data?: { title?: string; description?: string; options?: any };
    }) => surveyReportApi.generateFromAudit(auditId, data),
    onSuccess: (response, { auditId }) => {
      queryClient.invalidateQueries({ queryKey: surveyReportQueryKeys.byAuditId(auditId) });
      queryClient.invalidateQueries({ queryKey: surveyReportQueryKeys.reports() });

      success('Report generated successfully');
    },
    onError: (err: any) => {
      const errorMessage = err?.response?.data?.message || 'Failed to generate report';
      error(errorMessage);
    },
  });
};

// Note: Publish functionality removed - reports are automatically available after generation

// Get Report Statistics
export const useReportStatistics = (reportId: string, options?: UseSurveyReportOptions) => {
  return useQuery({
    queryKey: surveyReportQueryKeys.statistics(reportId),
    queryFn: () => surveyReportApi.getReportStatistics(reportId),
    enabled: options?.enabled !== false && !!reportId,
    staleTime: options?.staleTime || 10 * 60 * 1000, // 10 minutes for statistics
  });
};

// Get Report Permissions
export const useReportPermissions = (reportId: string, options?: UseSurveyReportOptions) => {
  return useQuery({
    queryKey: surveyReportQueryKeys.permissions(reportId),
    queryFn: () => surveyReportApi.checkReportPermissions(reportId),
    enabled: options?.enabled !== false && !!reportId,
    staleTime: options?.staleTime || 5 * 60 * 1000,
  });
};

// Get PDF Exports
export const usePdfExports = (reportId: string, options?: UseSurveyReportOptions) => {
  return useQuery({
    queryKey: surveyReportQueryKeys.exports(reportId),
    queryFn: () => surveyReportApi.listPdfExports(reportId),
    enabled: options?.enabled !== false && !!reportId,
    staleTime: options?.staleTime || 2 * 60 * 1000,
  });
};

// Generate PDF Export
export const useGeneratePdfExport = () => {
  const queryClient = useQueryClient();
  const { success, error } = useNotifications();

  return useMutation({
    mutationFn: ({
      reportId,
      options,
    }: {
      reportId: string;
      options?: {
        includeComments?: boolean;
        includeStatistics?: boolean;
        format?: 'A4' | 'LETTER';
        orientation?: 'portrait' | 'landscape';
        includeRawData?: boolean;
      };
    }) => surveyReportApi.generatePdfExport(reportId, options),
    onSuccess: (response, { reportId }) => {
      queryClient.invalidateQueries({ queryKey: surveyReportQueryKeys.exports(reportId) });

      success('PDF export generated successfully');
    },
    onError: (err: any) => {
      const errorMessage = err?.response?.data?.message || 'Failed to generate PDF export';
      error(errorMessage);
    },
  });
};

// Download PDF Export
export const useDownloadPdfExport = () => {
  const { error } = useNotifications();

  return useMutation({
    mutationFn: ({ reportId, exportId }: { reportId: string; exportId: string }) =>
      surveyReportApi.downloadPdfExport(reportId, exportId),
    onSuccess: (response) => {
      // Open download URL in new tab
      if (response.data?.downloadUrl) {
        window.open(response.data.downloadUrl, '_blank');
      }
    },
    onError: (err: any) => {
      const errorMessage = err?.response?.data?.message || 'Failed to download PDF';
      error(errorMessage);
    },
  });
};

// Delete PDF Export
export const useDeletePdfExport = () => {
  const queryClient = useQueryClient();
  const { success, error } = useNotifications();

  return useMutation({
    mutationFn: ({ reportId, exportId }: { reportId: string; exportId: string }) =>
      surveyReportApi.deletePdfExport(reportId, exportId),
    onSuccess: (response, { reportId }) => {
      queryClient.invalidateQueries({ queryKey: surveyReportQueryKeys.exports(reportId) });
      success('PDF export deleted successfully');
    },
    onError: (err: any) => {
      const errorMessage = err?.response?.data?.message || 'Failed to delete PDF export';
      error(errorMessage);
    },
  });
};
